import { CalibrationDisplaySchema, CalibrationPointsConfig, RawFrequencyPoint } from "../types";
import {
  formatScientific,
  formatGaugeFactor,
  formatTempCoeff,
  formatPercentFS,
  formatDigit,
  formatDisplacementMm,
} from "../formatters";
import { normalizeCategory, matchesCategory } from "../categoryUtils";

const isPiezometerCategory = (data: any): boolean =>
  matchesCategory(normalizeCategory(data?.category), "piezometer");

const defaultCalibrationPoints: CalibrationPointsConfig = {
  increasing: [10, 20, 30, 40, 50],
  decreasing: [40, 30, 20, 10, 0],
  secondIncreasing: [10, 20, 30, 40, 50]
};

const generateVWPlaceholder = (point: number, sequence: "inc" | "dec" | "inc2"): string => {
  const baseValue = 1800;
  if (sequence === "inc2") {
    return `${baseValue + point * 5 + 0.2}`;
  }
  return `${baseValue + point * 5}`;
};

// Format a step value without rounding to a fixed number of decimals,
// only trimming floating-point noise (e.g. 0.28 must stay "0.28", not "0.3").
const formatStepValue = (value: number): string => {
  const rounded = Math.round(value * 1e6) / 1e6;
  return rounded.toString();
};

const generateDisplacementTemplateLabels = (
  range: string
): { frequencyLabels: string[]; temperatureLabels: string[] } | null => {
  // Extract numeric value from range string (e.g., "25MM" -> 25, "50MM" -> 50)
  const rangeMatch = range.match(/(\d+\.?\d*)/);
  if (!rangeMatch) return null;

  const rangeValue = parseFloat(rangeMatch[1]);
  if (isNaN(rangeValue) || rangeValue <= 0) return null;

  // Calculate step size (range divided by 5)
  const step = rangeValue / 5;

  // Generate frequency labels
  // Pattern: BOL, AOL, then increasing (step, 2*step, 3*step, 4*step, 5*step),
  // then decreasing (4*step, 3*step, 2*step, step, 0),
  // then second increasing (step, 2*step, 3*step, 4*step, 5*step)
  const frequencyLabels: string[] = ["BOL", "AOL"];

  // First increasing sequence
  for (let i = 1; i <= 5; i++) {
    frequencyLabels.push(formatStepValue(i * step));
  }

  // Decreasing sequence
  for (let i = 4; i >= 1; i--) {
    frequencyLabels.push(formatStepValue(i * step));
  }
  frequencyLabels.push("0");

  // Second increasing sequence
  for (let i = 1; i <= 5; i++) {
    frequencyLabels.push(formatStepValue(i * step));
  }

  // Temperature labels are always the same
  const temperatureLabels = ["Room T", "High T", "Room F", "High temp F"];

  return { frequencyLabels, temperatureLabels };
};

const transformDisplacementToRawFrequency = (
  formData: Record<string, unknown>,
  rangeValue: number
): RawFrequencyPoint[] => {
  const rawFrequency: RawFrequencyPoint[] = [];
  const step = rangeValue / 5;
  const displacements = [0, step, step * 2, step * 3, step * 4, step * 5];

  const getCalibrationPointValue = (fieldName: string): number | null => {
    const value = formData[fieldName];
    return typeof value === "string" && value.trim() !== "" ? parseFloat(value) : null;
  };

  const getPressureOrDisplacement = (calibrationPoint: number, rangeValue: number): number => {
    const step = rangeValue / 5;
    const expectedValues = [0, step, 2 * step, 3 * step, 4 * step, 5 * step];

    const exactMatch = expectedValues.find(disp => Math.abs(disp - calibrationPoint) < 0.01);
    if (exactMatch !== undefined) return exactMatch;

    if (calibrationPoint > 0 && calibrationPoint % step === 0) {
      const index = calibrationPoint / step;
      if (index >= 0 && index <= 5 && Number.isInteger(index)) {
        return calibrationPoint;
      }
    }

    if (calibrationPoint % 10 === 0 && calibrationPoint <= 50 && calibrationPoint % step !== 0) {
      const index = calibrationPoint / 10;
      return Math.min(index, 5) * step;
    }

    const percentageToIndex: Record<number, number> = { 60: 3, 80: 4, 100: 5 };
    if (percentageToIndex.hasOwnProperty(calibrationPoint)) {
      return percentageToIndex[calibrationPoint] * step;
    }

    if (calibrationPoint <= 100) {
      const index =
        calibrationPoint <= 20 ? 1 :
          calibrationPoint <= 40 ? 2 :
            calibrationPoint <= 60 ? 3 :
              calibrationPoint <= 80 ? 4 : 5;
      return index * step;
    }

    const index = Math.min(Math.round(calibrationPoint / 10), 5);
    return displacements[index] ?? 0;
  };

  // AOL should be the first entry in raw_frequency at displacement 0
  // BOL is handled separately in input_data.BOL, not in raw_frequency
  let aolFrequency: number | null = null;
  if (typeof formData.AOL === "string" && formData.AOL.trim() !== "") {
    const aolValue = parseFloat(formData.AOL);
    if (!isNaN(aolValue) && aolValue !== 0) {
      aolFrequency = aolValue;
    }
  }

  const detectCalibrationPoints = (): number[] => {
    const points: number[] = [];

    Object.keys(formData).forEach(key => {
      const match = key.match(/^point_(\d+(?:_\d+)*)_(inc|dec|inc2)$|^point_0$/);
      if (!match) return;

      // Ignore empty fields. Leftover default-template fields (point_10_inc …
      // point_50_inc) are empty strings; including them pollutes the
      // index-based mapping below and breaks any range whose real points
      // exceed the defaults (e.g. a 100 mm sensor: 20..100).
      if (getCalibrationPointValue(key) === null) return;

      const pointValue =
        key === "point_0"
          ? 0
          : parseFloat(match[1].replace(/_/g, "."));

      if (!isNaN(pointValue) && pointValue >= 0) {
        if (!points.includes(pointValue)) points.push(pointValue);
      }
    });

    return points.sort((a, b) => a - b);
  };

  const availablePoints = detectCalibrationPoints();

  const extractPoints = (suffix: "inc" | "dec" | "inc2", desc?: boolean): number[] => {
    const list = availablePoints.filter(point => {
      if (point === 0 && suffix !== "dec") {
        return false;
      }
      const name = point === 0 ? "point_0" : `point_${point.toString().replace(/\./g, "_")}_${suffix}`;
      return getCalibrationPointValue(name) !== null;
    });
    return list.sort((a, b) => (desc ? b - a : a - b));
  };

  const process = (points: number[], suffix: "inc" | "dec" | "inc2") => {
    points.forEach(point => {
      const name = point === 0 ? "point_0" : `point_${point.toString().replace(/\./g, "_")}_${suffix}`;
      const frequency = getCalibrationPointValue(name);
      if (frequency !== null) {
        rawFrequency.push({
          displacement: getPressureOrDisplacement(point, rangeValue),
          frequency
        });
      }
    });
  };

  // Add AOL as the first entry at displacement 0 if it exists
  if (aolFrequency !== null) {
    rawFrequency.unshift({
      displacement: 0,
      frequency: aolFrequency
    });
  }

  process(extractPoints("inc"), "inc");
  process(extractPoints("dec", true), "dec");
  process(extractPoints("inc2"), "inc2");

  return rawFrequency;
};

export const displacementSensorSchema: CalibrationDisplaySchema = {
  sensorType: "displacement",
  unit: "mm",

  inputFieldConfig: {
    basicFields: ["BOL", "AOL"],
    calibrationPoints: defaultCalibrationPoints,
    temperatureFields: ['roomT', 'roomF', 'highT', 'highTempF'],
    additionalFields: [
      {
        key: 'dwtTraceable',
        label: 'Equipment Calibration Certificate Number',
        type: 'text',
        unit: '',
        placeholder: 'Certificate number',
        required: true, // Mandatory for Displacement
        condition: (data: any) => {

          const category = normalizeCategory(data?.category);
          if (!category) return false;
          return !isPiezometerCategory({ category });
        }
      }
    ],
    generatePlaceholder: generateVWPlaceholder,
    transformToRawFrequency: transformDisplacementToRawFrequency,
    generateTemplateLabels: generateDisplacementTemplateLabels
  },

  certificateFormConfig: {
    title: "Displacement Certificate Details",
    fields: [
      {
        key: "date",
        label: "Date",
        type: "date",
        placeholder: "DD.MM.YYYY"
      },
      {
        key: "temperature",
        label: "Ambient Temperature",
        type: "text",
        placeholder: "e.g., 26 °C",
        unit: "°C"
      },
      {
        key: "cableLength",
        label: "Cable Length",
        type: "text",
        placeholder: "Leave blank if not applicable",
        unit: "Meter"
      },
      {
        key: "dwtNo",
        label: "Equipment Calibration Certificate Number",
        type: "text",
        placeholder: "Enter certificate number",
        required: true
      }
    ]
  },

  inputData: {
    sections: [
      {
        title: "Basic Calibration Information",
        bgColor: "bg-blue-50",
        group: "setup",
        fields: [
          {
            key: "raw_frequency.0.frequency",
            label: "AOL",
            unit: "",
            condition: (data: any) => {
              const first = data?.raw_frequency?.[0];
              if (!first) return false;
              const measurementValue = first.displacement ?? first.pressure;
              return measurementValue === 0 && first.frequency != null;
            }
          },
          { key: "gauge_factor", label: "Gauge Factor", unit: (ctx) => `${ctx.measurementUnit}/digit`, format: formatGaugeFactor },
          { key: "max_error_percent_fs", label: "Max Error %FS", unit: "%", format: formatPercentFS },
          { key: "max_error_poly_fit_percent_fs", label: "Max Error Poly Fit %FS", unit: "%", format: formatPercentFS }
        ]
      },
      {
        title: "ABC Coefficients",
        bgColor: "bg-gray-50",
        group: "setup",
        fields: [
          { key: "abc_coefficients.A", label: "Coefficient A", format: formatScientific },
          { key: "abc_coefficients.B", label: "Coefficient B", format: formatScientific },
          { key: "abc_coefficients.C", label: "Coefficient C", format: formatScientific }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "abc_coefficients" in data
      },
      {
        title: "Temperature Compensation",
        bgColor: "bg-yellow-50",
        group: "setup",
        fields: [
          { key: "digit_per_deg", label: "Digit/°C", unit: "", format: formatTempCoeff },
          { key: "mm_per_deg", label: "mm/°C", unit: "", format: formatScientific },
          { key: "percent_fs_per_deg", label: "%FS/°C", unit: "", format: formatTempCoeff }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "digit_per_deg" in data
      },
      {
        title: "Error Analysis",
        bgColor: "bg-red-50",
        group: "analysis",
        fields: [
          { key: "max_repeatability_percent_fs", label: "Max Repeatability %FS", unit: "%", format: formatPercentFS },
          { key: "max_hysteresis_percent_fs", label: "Max Hysteresis %FS", unit: "%", format: formatPercentFS }
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          ("max_repeatability_percent_fs" in data || "max_hysteresis_percent_fs" in data)
      },
      {
        title: "Environmental Conditions",
        bgColor: "bg-cyan-50",
        group: "setup",
        fields: [
          { key: "ambient_temperature", label: "Ambient Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
          { key: "weather_condition", label: "Weather Condition", unit: "" },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          ("ambient_temperature" in data || "humidity" in data),
      },
    ],

    tables: [
      {
        title: "Temperature Frequency Data",
        bgColor: "bg-yellow-50",
        group: "measurement",
        dataKey: "temperature_frequency",
        columns: [
          { key: "temperature", label: "Temperature (°C)", unit: "" },
          { key: "frequency", label: "Frequency", unit: "" },
          { key: "condition", label: "Condition", unit: "" }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "temperature_frequency" in data
      },
      {
        title: "Raw Frequency & Digits Data",
        bgColor: "bg-green-50",
        group: "measurement",
        dataKey: "raw_frequency",
        secondaryDataKey: "raw_digits",
        mergeOn: ["displacement", "cycle"],
        columns: [
          { key: "displacement", label: "Displacement (mm)", unit: "", format: formatDisplacementMm },
          { key: "frequency", label: "Frequency", className: "font-mono", unit: "" },
          { key: "digit", label: "Digit", className: "font-mono", unit: "", format: formatDigit },
          { key: "cycle", label: "Cycle", unit: "" }
        ],
        rowClassName: (row: unknown) =>
          typeof row === "object" && row !== null && "cycle" in row && (row as any).cycle === "increment"
            ? "bg-blue-50"
            : "bg-orange-50",
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "raw_frequency" in data
      },
      {
        title: "Average Points Analysis",
        bgColor: "bg-indigo-50",
        group: "analysis",
        dataKey: "average_points",
        columns: [
          { key: "displacement", label: "Displacement (mm)", className: "font-medium", unit: "", format: formatDisplacementMm },
          { key: "digit_avg", label: "Digit Avg", className: "font-mono", unit: "", format: formatDigit },
          { key: "relative_digit", label: "Relative Digit", className: "font-mono", unit: "", format: formatDigit },
          { key: "end_fit_displacement", label: "End Fit (mm)", className: "font-mono", unit: "", format: formatDisplacementMm },
          { key: "error_end_fit_percent_fs", label: "Error End Fit %FS", className: "font-mono", unit: "%", format: formatPercentFS },
          { key: "poly_fit_displacement", label: "Poly Fit (mm)", className: "font-mono", unit: "", format: formatDisplacementMm },
          { key: "error_poly_fit_percent_fs", label: "Error Poly Fit %FS", className: "font-mono", unit: "%", format: formatPercentFS }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "average_points" in data
      }
    ]
  },

  outputData: {
    sections: [
      {
        title: "Summary",
        bgColor: "bg-green-50",
        group: "result",
        fields: [
          {
            key: "raw_frequency.0.frequency",
            label: "AOL",
            unit: "",
            condition: (data: any) => {
              const first = data?.raw_frequency?.[0];
              if (!first) return false;
              const measurementValue = first.displacement ?? first.pressure;
              return measurementValue === 0 && first.frequency != null;
            }
          },
          { key: "gauge_factor", label: "Gauge Factor", unit: (ctx) => `${ctx.measurementUnit}/digit`, format: formatGaugeFactor },
          { key: "Nl_percent_fs", label: "NL %FS", unit: "%", format: formatPercentFS },
          { key: "Acc_percent_fs", label: "Acc %FS", unit: "%", format: formatPercentFS },
          { key: "Rep_percent_fs", label: "Rep %FS", unit: "%", format: formatPercentFS },
          { key: "Hys_percent_fs", label: "Hys %FS", unit: "%", format: formatPercentFS },
          { key: "mm_per_degC", label: "mm/°C", unit: "", format: formatScientific },
          { key: "percent_fs_per_degC", label: "%FS/°C", unit: "", format: formatTempCoeff }
        ]
      },
      {
        title: "Coefficients",
        bgColor: "bg-blue-50",
        group: "result",
        fields: [
          { key: "A", label: "Coefficient A", format: formatScientific },
          { key: "B", label: "Coefficient B", format: formatScientific },
          { key: "C", label: "Coefficient C", format: formatScientific }
        ]
      }
    ],

  }
};
